import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, ClockIcon, BanIcon, CheckCircleIcon, TrashIcon, DuplicateIcon } from '@heroicons/react/outline';
import { formatDispatchTime, calculateCutoffTime } from '../utils';
import { realCarrierName } from '../../../components';

const T_PREFIX = 'lastmile_management.dispatch_schedules';

const TimeGroupItem = ({
    schedule,
    shippingMethods = [],
    canEdit,
    onEditSchedule,
    onViewHistory,
    onToggleEnable,
    onDeleteSchedule,
    onDuplicateSchedule,
    isToggleLoading,
    selectionMode = false,
    isSelected = false,
    onToggleSelect,
}) => {
    const { i18n } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const isIntegration = !schedule.carrier_name && !schedule.shipping_method_id && !!schedule.channel_type_name;
    const isShippingMethod = !!schedule.shipping_method_id;

    let carrierLabel;
    if (isShippingMethod) {
        const sm = shippingMethods.find(m => m.id === schedule.shipping_method_id);
        // Show the REAL carrier on dispatch management surfaces (CUBBO-2185).
        const smCarrier = sm && realCarrierName(sm.carrier_name, sm.internal_carrier_name);
        carrierLabel = sm
            ? `${sm.shipping_name}${smCarrier ? ` (${smCarrier})` : ''}`
            : schedule.shipping_method_name || '—';
    } else {
        carrierLabel = realCarrierName(schedule.carrier_name, schedule.internal_carrier_name) || schedule.channel_type_name || '—';
    }

    // Color scheme based on type and enable state
    const disabled = !schedule.enable;
    const hoverBg = disabled ? 'bg-gray-500'
        : isShippingMethod ? 'bg-green-500' : isIntegration ? 'bg-yellow-500' : 'bg-blue-500';
    const hoverBtnStyle = disabled
        ? 'p-0.5 text-gray-200 hover:text-white rounded-full hover:bg-gray-600 transition-colors'
        : isShippingMethod
            ? 'p-0.5 text-green-100 hover:text-white rounded-full hover:bg-green-600 transition-colors'
            : isIntegration
                ? 'p-0.5 text-yellow-100 hover:text-white rounded-full hover:bg-yellow-600 transition-colors'
                : 'p-0.5 text-blue-100 hover:text-white rounded-full hover:bg-blue-600 transition-colors';
    const hoverDeleteStyle = disabled
        ? 'p-0.5 text-gray-200 hover:text-red-200 rounded-full hover:bg-gray-600 transition-colors'
        : isShippingMethod
            ? 'p-0.5 text-green-100 hover:text-red-200 rounded-full hover:bg-green-600 transition-colors'
            : isIntegration
                ? 'p-0.5 text-yellow-100 hover:text-red-200 rounded-full hover:bg-yellow-600 transition-colors'
                : 'p-0.5 text-blue-100 hover:text-red-200 rounded-full hover:bg-blue-600 transition-colors';
    const labelColor = isShippingMethod ? 'text-green-700' : isIntegration ? 'text-yellow-700' : 'text-gray-600';

    const handleItemDragStart = (e) => {
        e.stopPropagation();
        e.dataTransfer.setData('application/x-dispatch-schedule', JSON.stringify({ scheduleIds: [schedule.id] }));
        e.dataTransfer.effectAllowed = 'move';
        setIsDragging(true);
    };

    return (
        <div
            draggable={canEdit && !selectionMode}
            onDragStart={canEdit && !selectionMode ? handleItemDragStart : undefined}
            onDragEnd={canEdit && !selectionMode ? () => setIsDragging(false) : undefined}
            className={`flex flex-col px-2 py-1 rounded transition-all ${
                canEdit ? 'cursor-pointer' : ''
            } ${isDragging ? 'opacity-30' : ''} ${
                isSelected ? 'ring-2 ring-indigo-400 bg-indigo-50' : ''
            } ${
                schedule.enable
                    ? (isHovered && canEdit && !selectionMode ? hoverBg : 'hover:bg-indigo-50')
                    : (isHovered && canEdit && !selectionMode ? hoverBg : 'bg-gray-100')
            }`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => {
                if (selectionMode) {
                    onToggleSelect?.(schedule.id);
                } else {
                    canEdit && onEditSchedule?.(schedule);
                }
            }}
        >
            {selectionMode ? (
                <div className="flex items-center justify-center gap-1">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => { e.stopPropagation(); onToggleSelect?.(schedule.id); }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-3 w-3 text-indigo-600 border-gray-300 rounded flex-shrink-0 cursor-pointer"
                    />
                    <span className={`text-[10px] font-semibold uppercase tracking-wide truncate ${labelColor}`}>
                        {carrierLabel}
                        {schedule.shipping_state && (
                            <span
                                className={`font-normal ${isHovered && canEdit ? 'text-white text-opacity-70' : 'text-purple-600'}`}
                                title={`${i18n.t(`${T_PREFIX}.filters.shipping_state`)}: ${schedule.shipping_state}`}
                            >
                                {' → '}{schedule.shipping_state}
                            </span>
                        )}
                    </span>
                </div>
            ) : (
                <span className={`text-[10px] font-semibold uppercase tracking-wide truncate ${
                    isHovered && canEdit ? 'text-white' : labelColor
                }`}>
                    {carrierLabel}
                    {schedule.shipping_state && (
                        <span
                            className={`font-normal ${isHovered && canEdit ? 'text-white text-opacity-70' : 'text-purple-600'}`}
                            title={`${i18n.t(`${T_PREFIX}.filters.shipping_state`)}: ${schedule.shipping_state}`}
                        >
                            {' → '}{schedule.shipping_state}
                        </span>
                    )}
                </span>
            )}

            {!schedule.enable && (
                <span className="text-[8px] font-medium uppercase text-gray-400 tracking-wider">
                    {i18n.t(`${T_PREFIX}.disabled_label`)}
                </span>
            )}

            {canEdit && !selectionMode && (
                <div className={`flex items-center justify-center gap-0.5 mt-0.5 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0 focus-within:opacity-100'}`}>
                    <button
                        onClick={(e) => { e.stopPropagation(); onViewHistory?.(schedule); }}
                        className={hoverBtnStyle}
                        title={i18n.t(`${T_PREFIX}.history.title`)}
                    >
                        <ClockIcon className="h-3 w-3" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleEnable?.(schedule); }}
                        className={hoverBtnStyle}
                        title={schedule.enable ? i18n.t(`${T_PREFIX}.actions.disable`) : i18n.t(`${T_PREFIX}.actions.enable`)}
                        disabled={isToggleLoading}
                    >
                        {schedule.enable
                            ? <BanIcon className="h-3 w-3" />
                            : <CheckCircleIcon className="h-3 w-3" />
                        }
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onEditSchedule?.(schedule); }}
                        className={hoverBtnStyle}
                        title={i18n.t(`${T_PREFIX}.edit_schedule`)}
                    >
                        <PencilIcon className="h-3 w-3" />
                    </button>
                    {onDuplicateSchedule && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDuplicateSchedule(schedule); }}
                            className={hoverBtnStyle}
                            title={i18n.t(`${T_PREFIX}.actions.duplicate`)}
                        >
                            <DuplicateIcon className="h-3 w-3" />
                        </button>
                    )}
                    {onDeleteSchedule && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDeleteSchedule(schedule); }}
                            className={hoverDeleteStyle}
                            title={i18n.t(`${T_PREFIX}.actions.delete`)}
                        >
                            <TrashIcon className="h-3 w-3" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const EMPTY_SET = new Set();

export const DispatchScheduleTimeGroup = ({
    schedules,
    shippingMethods = [],
    canEdit = false,
    onEditSchedule,
    onViewHistory,
    onToggleEnable,
    onDeleteSchedule,
    onDuplicateSchedule,
    isToggleLoading = false,
    pendingMoveIds = EMPTY_SET,
    selectionMode = false,
    selectedIds = EMPTY_SET,
    onToggleSelect,
    dayLabel
}) => {
    const { i18n } = useTranslation();
    const [isDragging, setIsDragging] = useState(false);

    const firstSchedule = schedules[0];
    const time = formatDispatchTime(firstSchedule.dispatch_time);
    const cutoffData = calculateCutoffTime(firstSchedule.dispatch_time, firstSchedule.time_before_dispatch);
    const isMoving = schedules.some(s => pendingMoveIds.has(s.id));

    const handleDragStart = (e) => {
        const scheduleIds = schedules.map(s => s.id);
        e.dataTransfer.setData('application/x-dispatch-schedule', JSON.stringify({ scheduleIds }));
        e.dataTransfer.effectAllowed = 'move';
        setIsDragging(true);
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    const borderClass = 'border-indigo-200';
    const timeColor = 'text-indigo-600';

    return (
        <div
            className={`rounded-md border ${borderClass} bg-white text-center ${
                isDragging ? 'opacity-30' : ''
            }`}
        >
            {/* Shared time header — draggable to move the entire group */}
            <div
                draggable={canEdit && !selectionMode}
                onDragStart={canEdit && !selectionMode ? handleDragStart : undefined}
                onDragEnd={canEdit && !selectionMode ? handleDragEnd : undefined}
                onClick={selectionMode && onToggleSelect ? () => {
                    const groupIds = schedules.map(s => s.id);
                    const allSelected = groupIds.every(id => selectedIds.has(id));
                    groupIds.forEach(id => {
                        if (allSelected) {
                            if (selectedIds.has(id)) onToggleSelect(id);
                        } else {
                            if (!selectedIds.has(id)) onToggleSelect(id);
                        }
                    });
                } : undefined}
                className={`px-2 pt-1.5 pb-1 ${selectionMode ? 'cursor-pointer' : canEdit ? 'cursor-grab' : ''}`}
            >
                <div className="flex items-center justify-center gap-1 relative">
                    {dayLabel && <span className="absolute -top-1 -right-1 text-[6px] font-normal text-gray-300 uppercase">{dayLabel}</span>}
                    <span className={`text-sm font-bold ${timeColor} leading-tight`}>{time}</span>
                    {isMoving && (
                        <span className="text-[9px] font-semibold uppercase text-amber-600 bg-amber-50 px-1 rounded animate-pulse">
                            {i18n.t(`${T_PREFIX}.moving`)}
                        </span>
                    )}
                </div>
                {cutoffData && (
                    <div className="text-xs text-gray-500">
                        {i18n.t(`${T_PREFIX}.cutoff_label`)}: {cutoffData.time}
                        {cutoffData.daysBefore > 0 && (
                            <span className="text-orange-600 font-medium">
                                {' '}({i18n.t(`${T_PREFIX}.cutoff_days_before`, { count: cutoffData.daysBefore })})
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Individual carrier items */}
            <div className="border-t border-indigo-100 px-1 py-1 space-y-0.5">
                {schedules.map(schedule => (
                    <TimeGroupItem
                        key={schedule.id}
                        schedule={schedule}
                        shippingMethods={shippingMethods}
                        canEdit={canEdit}
                        onEditSchedule={onEditSchedule}
                        onViewHistory={onViewHistory}
                        onToggleEnable={onToggleEnable}
                        onDeleteSchedule={onDeleteSchedule}
                        onDuplicateSchedule={onDuplicateSchedule}
                        isToggleLoading={isToggleLoading}
                        selectionMode={selectionMode}
                        isSelected={selectedIds.has(schedule.id)}
                        onToggleSelect={onToggleSelect}
                    />
                ))}
            </div>
        </div>
    );
};
